// Retailer "A Day in the Life" autopilot demo (12 steps).
//
// Pure scripted overlay via @tkos/demo-engine — simulated cards/messages on top
// of the real portal. No API calls, no payments, no real data touched.
// Triggered by ?demo=retailer (the admin Demo page opens the portal that way).

import { startDemo, type DemoStep, type DemoHandle } from "@tkos/demo-engine";

const KIT_FAB = '.tkos-fab [data-fab="kit"]';

const STEPS: DemoStep[] = [
  {
    name: "Website lead overnight",
    narration: "Aria captured a lead at 11pm from the website chat widget.",
    run: (d) => {
      d.highlight(KIT_FAB, { label: "Kit" });
      d.showAlertCard({ icon: "✨", title: "New lead — Sarah Johnson", detail: "£15k budget, Windsor range. Came via website chat." });
    },
  },
  {
    name: "Morning briefing",
    narration: "Kit's morning briefing summarises the day.",
    run: (d) => {
      d.highlight(KIT_FAB, { label: "Kit" });
      d.showCard(`<div style="font-weight:800;margin-bottom:6px">Kit — Morning briefing</div>
        <div style="font-size:.86rem;line-height:1.5">Good morning Steve. Here's what needs your attention today:<br>• 1 new lead<br>• 2 quotes unaccepted<br>• 1 payment overdue</div>`);
    },
  },
  {
    name: "Kit chases the lead",
    narration: "One instruction. Kit handles the rest.",
    durationMs: 8000,
    run: async (d) => {
      d.showMessage({ side: "out", from: "You → Kit", text: "Kit, contact Sarah Johnson and book a showroom visit" });
      await d.wait(900);
      d.showCard(`<div style="font-weight:800;margin-bottom:6px">Kit will do 3 things — confirm?</div>
        <ol style="margin:0;padding-left:1.1rem;font-size:.84rem;line-height:1.7">
          <li>Message Sarah to arrange a visit</li>
          <li>Add a calendar entry — showroom visit</li>
          <li>Create a task — follow up Sarah Johnson</li>
        </ol>
        <div style="margin-top:8px;background:#E8A020;color:#1a1206;font-weight:800;border-radius:8px;padding:.4rem;text-align:center">✓ Confirm all three</div>`);
      await d.wait(1200);
      d.toast("Done — message sent, visit booked, task created");
    },
  },
  {
    name: "Quote built",
    narration: "Quote built in minutes with live supplier pricing.",
    run: (d) => {
      d.highlight("#tkos-main", { label: "Quote Builder" });
      d.showCard(`<div style="font-weight:800;margin-bottom:6px">Quote Builder — Sarah Johnson</div>
        <div style="font-size:.84rem;color:#6b7280">TKC · Windsor range · live trade pricing</div>
        <div style="margin-top:8px;font-size:1.4rem;font-weight:800">£14,200</div>`, { wide: true });
    },
  },
  {
    name: "Quote sent",
    narration: "Customer portal created automatically. Professional, branded.",
    run: (d) => {
      d.showCard(`<div style="font-weight:800">Quote sent to Sarah Johnson ✓</div>
        <div style="font-size:.84rem;color:#6b7280;margin-top:4px">Branded customer portal created. Notification sent.</div>`);
    },
  },
  {
    name: "Kit alert — quote opened",
    narration: "Kit knows when the customer opens their quote.",
    run: (d) => {
      d.highlight(KIT_FAB, { label: "Kit" });
      d.showAlertCard({ icon: "👀", tone: "warn", title: "Sarah Johnson opened her quote", detail: "Opened 48 hours ago — not yet accepted." });
    },
  },
  {
    name: "Kit follows up",
    narration: "Kit follows up in the retailer's voice.",
    run: async (d) => {
      d.showMessage({ side: "out", from: "Kit → Sarah", text: "Hi Sarah! Just checking you had everything you need on your kitchen quote — any questions, I'm here to help. 😊" });
      await d.wait(900);
      d.toast("Follow-up sent · alert auto-dismissed");
    },
  },
  {
    name: "Quote accepted, deposit requested",
    narration: "Customer accepts. Deposit requested automatically.",
    run: (d) => {
      d.showAlertCard({ icon: "✅", tone: "success", title: "Quote accepted — Sarah Johnson", detail: "Status → Approved. Deposit request sent automatically." });
    },
  },
  {
    name: "Payment received",
    narration: "Money in. Kit notifies.",
    run: (d) => {
      d.highlight(KIT_FAB, { label: "Kit" });
      d.showAlertCard({ icon: "💷", tone: "success", title: "Deposit received — Sarah Johnson £4,260", detail: "TKOS commission £140.58" });
    },
  },
  {
    name: "Delivery booked",
    narration: "Kit books the delivery and confirms with the customer.",
    run: async (d) => {
      d.showCard(`<div style="font-weight:800">Delivery booked — Mon 21 July</div><div style="font-size:.84rem;color:#6b7280;margin-top:4px">Added to calendar.</div>`);
      await d.wait(900);
      d.showMessage({ side: "out", from: "Kit → Sarah", text: "Great news Sarah — your kitchen is booked in for delivery on Monday 21st July. We'll confirm a time the week before." });
    },
  },
  {
    name: "Snag logged by customer",
    narration: "Customer logs a snag through Aria at 10pm.",
    run: (d) => {
      d.highlight(KIT_FAB, { label: "Kit" });
      d.showAlertCard({ icon: "🛠️", tone: "warn", title: "Snag logged by Sarah Johnson", detail: "Scratched door on unit 4. Customer-logged." });
    },
  },
  {
    name: "Pipeline summary",
    narration: "End of day. Kit knows exactly where the business stands.",
    durationMs: 7000,
    run: async (d) => {
      d.showMessage({ side: "out", from: "You → Kit", text: "How's the business looking?" });
      await d.wait(900);
      d.showCard(`<div style="font-weight:800;margin-bottom:6px">Kit — Business summary</div>
        <div style="font-size:.86rem;line-height:1.6">14 active jobs · <b>£87k</b> quoted · <b>£23k</b> approved · <b>£8k</b> outstanding · 3 deliveries this week.</div>`, { wide: true });
    },
  },
];

let handle: DemoHandle | null = null;

/** Start the retailer demo if the URL says so (?demo=retailer). */
export function maybeStartRetailerDemo(): void {
  try {
    if (new URLSearchParams(window.location.search).get("demo") !== "retailer") return;
  } catch { return; }
  if (handle) return;
  // Let the shell paint first so highlight targets exist.
  window.setTimeout(() => { handle = startDemo({ title: "Retailer Demo — A Day in the Life", steps: STEPS }); }, 600);
}
